import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';
// integrated-teams/{your-team}/message-templates/{message-template}/src/placements/in-app-v2/{placement-id}/index.tsx

import { type BackendPlacementMessage } from '@post-office/message-maker-node/placements/types';
import { noValidation } from '@post-office/message-maker-node/placements/message-helpers';
import {
	PlacementBackend,
	PlacementFrontend,
} from '@post-office/placements--growth/confluence-side-nav';
import { RovoCcpId } from '@post-office/message-template-contracts/message-template-metadata';
import type { TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

// assertMessageTemplateContext is a validation file created by Post Office's validation library Vindicated. It will be included when you create your message template. You can find out more about vindicated here: https://developer.atlassian.com/platform/post-office/working-with-post-office/technical-deep-dives/vindicated-validation-library/#vindicated:-post-office's-runtime-type-validation-library

import { type MessageTemplateContext } from '../../..';
import { assertMessageTemplateContext } from '../../../__generated__/validation/messageTemplateContext';
import { LeftNavCtaMessage } from '../../../common/components/LeftNavCtaMessage';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import type {
	CreatePlacementMessageConfig,
	AnyPayload,
} from '@post-office/message-template-contracts/placement-v2';
import {
	type BackendSyncMessageStage,
	type AnyBackendMessageStage,
} from '@post-office/message-maker-node/placements/types';

// Currently we won't look into the message backend, but this is where you can add stages which
// change your message before it gets sent to the frontend.
export const MessageBackend: (config: CreatePlacementMessageConfig) => BackendPlacementMessage<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = PlacementBackend.createMessageBackend(() => ({
	transformMetadata: {
		validate: assertMessageTemplateContext,
		stage: () => ({
			rerankingMetadata: { productId: RovoCcpId },
		}),
	},
}));

type MessageFrontendProps = MessageTemplateContext & {
	handleOnDismissed: () => void;
	experimentCohort: string;
	onSideNavClick: (nextBestActionType: NextBestAction) => string | void | null;
};

// This is the Message Frontend. In this Placement we have render.Component which takes a React Component which will render your message in the Placement.
export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<MessageTemplateContext>(),
		Component: (props: MessageFrontendProps) => {
			return (
				<LeftNavCtaMessage
					{...props}
					context={{
						experimentCohort: props.experimentCohort,
						source: 'confluence-side-nav',
					}}
				/>
			);
		},
	},
});
